import React from 'react';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SmartHeader, SmartField, NoRecordsCard } from './_SmartFieldRenderer';

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        {children}
    </div>
);

const AddressBlock = ({ label, value }) => (
    <div className="surface-100 border-round p-3 mb-2">
        {label && <div className="text-primary text-xs font-semibold mb-1">{label}</div>}
        <div className="text-900 text-sm" style={{ wordBreak: 'break-word' }}>
            {value || '—'}
        </div>
    </div>
);

// Surepass uses 1800-01-01 as a null-date sentinel for cancellation / Aadhaar
// validation dates. Treat it (and empty values) as "no date".
const isRealDate = (d) => Boolean(d) && d !== '1800-01-01' && d !== '0000-00-00';

const buildAddress = (a) => {
    if (!a) return '';
    if (typeof a === 'string') return a;
    return [
        a.address || a.adr || a.bno,
        a.street || a.st,
        a.location || a.loc,
        a.dst,
        a.city,
        a.state || a.stcd,
        a.pincode || a.pncd,
    ]
        .filter(Boolean)
        .join(', ');
};

const GSTINAdvancedOutputComponent = ({ reportData, hideCredits, isSharedView, onDownloadPDF, pdfPasswordHint }) => {
    const shouldHideCredits = hideCredits || isSharedView;

    if (!reportData) return <NoRecordsCard subtitle="No GSTIN data available" />;

    let gstinData = null;
    if (reportData.data?.data) gstinData = reportData.data.data;
    else if (reportData.report_json?.data?.data) gstinData = reportData.report_json.data.data;
    else if (reportData.data) gstinData = reportData.data;

    const remainingBalance = reportData?.remaining_credits ?? reportData?.data?.remaining_credits;

    if (!gstinData) {
        return <NoRecordsCard subtitle="No GST records found for the provided GSTIN. Please verify the number and try again." />;
    }

    const gstinStatus = gstinData?.gstin_status || gstinData?.status || '';
    const isActive = gstinStatus.toLowerCase() === 'active';

    // Surepass returns contact details under `contact_details.principal` / `.additional`.
    // The older key names (pradr/adadr, principal_address) are kept as fallbacks so this
    // component still renders reports fetched through other providers or older payloads.
    const principalContact = gstinData?.contact_details?.principal || null;

    const principalAddress =
        principalContact?.address ||
        gstinData?.principal_place_of_business_fields ||
        gstinData?.principal_address ||
        gstinData?.pradr ||
        null;

    const additionalAddresses =
        gstinData?.contact_details?.additional ||
        gstinData?.additional_place_of_business_fields ||
        gstinData?.additional_addresses ||
        gstinData?.adadr ||
        [];

    const filings = gstinData?.filing_status || gstinData?.filing_table || gstinData?.filings || [];

    const promoters = Array.isArray(gstinData?.promoters) ? gstinData.promoters : [];

    const rawActivity = gstinData?.nature_bus_activities || gstinData?.business_activity;
    const businessActivity = Array.isArray(rawActivity) ? rawActivity : rawActivity ? [rawActivity] : [];

    const filingStatusBody = (row) => {
        const v = row?.mof || row?.mode_of_filing || row?.status || '';
        const sev = String(v).toLowerCase() === 'online' ? 'success' : 'info';
        return v ? <Tag value={v} severity={sev} /> : '—';
    };

    return (
        <div>
            <SmartHeader title="GSTIN Advanced Report" icon="pi-building" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />

            <Card>
                <div className="flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    {onDownloadPDF ? (
                        <Button
                            label="Download PDF"
                            icon="pi pi-file-pdf"
                            size="small"
                            outlined
                            onClick={onDownloadPDF}
                        />
                    ) : <span />}
                    <Tag
                        icon={isActive ? 'pi pi-check-circle' : 'pi pi-times-circle'}
                        value={gstinStatus || 'Unknown'}
                        severity={isActive ? 'success' : 'danger'}
                    />
                </div>

                {onDownloadPDF && pdfPasswordHint && (
                    <div className="surface-100 border-round border-left-3 border-primary p-2 mb-2 flex align-items-start gap-2">
                        <i className="pi pi-lock text-primary text-sm mt-1" aria-hidden="true" />
                        <span className="text-600 text-sm line-height-3">{pdfPasswordHint}</span>
                    </div>
                )}

                <Section icon="pi-building" title="Business Identity">
                    <div className="grid">
                        <SmartField name="gstin" value={gstinData?.gstin || gstinData?.id_number} highlight />
                        <SmartField
                            name="legal_or_trade_name"
                            value={gstinData?.legal_name || gstinData?.trade_name || gstinData?.lgnm || gstinData?.tradenam}
                        />
                        {(gstinData?.trade_name || gstinData?.tradenam) && gstinData?.legal_name && (
                            <SmartField name="trade_name" value={gstinData.trade_name || gstinData.tradenam} />
                        )}
                        <SmartField name="taxpayer_type" value={gstinData?.taxpayer_type || gstinData?.dty} />
                        <SmartField name="constitution_of_business" value={gstinData?.constitution_of_business || gstinData?.ctb} />
                        {gstinData?.pan_number && <SmartField name="pan_number" value={gstinData.pan_number} />}
                    </div>
                </Section>

                <Section icon="pi-calendar" title="Registration Details">
                    <div className="grid">
                        <SmartField
                            name="date_of_registration"
                            value={gstinData?.date_of_registration || gstinData?.registration_date || gstinData?.rgdt}
                        />
                        {/* Surepass sends 1800-01-01 as a "not cancelled" sentinel — don't surface it. */}
                        {isRealDate(gstinData?.date_of_cancellation || gstinData?.cancellation_date || gstinData?.cxdt) && (
                            <SmartField
                                name="date_of_cancellation"
                                value={gstinData.date_of_cancellation || gstinData.cancellation_date || gstinData.cxdt}
                            />
                        )}
                        <SmartField name="last_updated" value={gstinData?.last_updated_date || gstinData?.lstupdt} />
                        {gstinData?.center_jurisdiction && <SmartField name="centre_jurisdiction" value={gstinData.center_jurisdiction} />}
                        {gstinData?.state_jurisdiction && <SmartField name="state_jurisdiction" value={gstinData.state_jurisdiction} />}
                    </div>
                </Section>

                {businessActivity.length > 0 && (
                    <Section icon="pi-briefcase" title="Nature of Business Activity">
                        <div className="flex flex-wrap gap-2">
                            {businessActivity.map((activity, idx) => (
                                <Chip key={idx} label={String(activity)} />
                            ))}
                        </div>
                    </Section>
                )}

                {principalAddress && (
                    <Section icon="pi-home" title="Principal Place of Business">
                        <AddressBlock value={buildAddress(principalAddress)} />
                        {principalContact && (
                            <div className="grid mt-2">
                                <SmartField name="email" value={principalContact.email} />
                                <SmartField name="mobile" value={principalContact.mobile} />
                                <SmartField name="nature_of_business" value={principalContact.nature_of_business} />
                            </div>
                        )}
                    </Section>
                )}

                {additionalAddresses.length > 0 && (
                    <Section icon="pi-map-marker" title={`Additional Places of Business (${additionalAddresses.length})`}>
                        {additionalAddresses.map((addr, idx) => (
                            <AddressBlock
                                key={idx}
                                label={`Location ${idx + 1}`}
                                value={buildAddress(addr?.address || addr)}
                            />
                        ))}
                    </Section>
                )}

                {promoters.length > 0 && (
                    <Section icon="pi-users" title={`Promoters / Directors (${promoters.length})`}>
                        <div className="flex flex-wrap gap-2">
                            {promoters.map((name, idx) => (
                                <Chip key={idx} label={String(name).trim()} icon="pi pi-user" />
                            ))}
                        </div>
                    </Section>
                )}

                <Section icon="pi-chart-bar" title="Compliance & Turnover">
                    <div className="grid">
                        <SmartField name="annual_turnover" value={gstinData?.annual_turnover} />
                        <SmartField name="annual_turnover_fy" value={gstinData?.annual_turnover_fy} />
                        <SmartField name="percentage_in_cash" value={gstinData?.percentage_in_cash} />
                        <SmartField name="aadhaar_validation" value={gstinData?.aadhaar_validation} />
                        {isRealDate(gstinData?.aadhaar_validation_date) && (
                            <SmartField name="aadhaar_validation_date" value={gstinData.aadhaar_validation_date} />
                        )}
                        <SmartField name="field_visit_conducted" value={gstinData?.field_visit_conducted} />
                        {(gstinData?.einvoice_status !== undefined || gstinData?.e_invoice_status !== undefined) && (
                            <SmartField
                                name="e_invoice_status"
                                value={(gstinData.einvoice_status ?? gstinData.e_invoice_status) ? 'Enabled' : 'Not Enabled'}
                            />
                        )}
                        <SmartField
                            name="core_business_activity"
                            value={
                                gstinData?.nature_of_core_business_activity_description
                                    ? `${gstinData.nature_of_core_business_activity_description}${
                                          gstinData.nature_of_core_business_activity_code
                                              ? ` (${gstinData.nature_of_core_business_activity_code})`
                                              : ''
                                      }`
                                    : null
                            }
                        />
                    </div>
                </Section>

                {filings.length > 0 && (
                    <Section icon="pi-file" title="Filing History">
                        <DataTable
                            value={filings}
                            size="small"
                            stripedRows
                            showGridlines
                            responsiveLayout="scroll"
                            paginator={filings.length > 10}
                            rows={10}
                            className="p-datatable-sm p-datatable-gridlines"
                        >
                            <Column field="ret_typ" header="Return Type" body={(row) => row?.ret_typ || row?.return_type || '—'} />
                            <Column field="fy" header="Financial Year" body={(row) => row?.fy || row?.financial_year || '—'} />
                            <Column field="taxp" header="Tax Period" body={(row) => row?.taxp || row?.tax_period || '—'} />
                            <Column field="dof" header="Date of Filing" body={(row) => row?.dof || row?.date_of_filing || '—'} />
                            <Column header="Status" body={filingStatusBody} />
                        </DataTable>
                    </Section>
                )}
            </Card>
        </div>
    );
};

export default GSTINAdvancedOutputComponent;
